// File: src/pages/BigCommerceLanding.jsx
// Simple landing page that handles redirects from backend /load endpoint
// The backend does all the heavy lifting - this just stores the token and navigates

import { useEffect, useState, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Spinner, Page, Layout, Frame, Text } from '@shopify/polaris';
import { AuthContext } from '../context/AuthContext';

export default function BigCommerceLanding() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Get params from backend redirect
    const token = params.get('token');
    const storeHash = params.get('store_hash');
    const siteId = params.get('site_id');
    const siteToken = params.get('site_token');
    const errorParam = params.get('error');
    const firstInstall = params.get('first_install');

    console.log('[BigCommerceLanding] Params:', { 
      hasToken: !!token, storeHash, siteId, firstInstall, error: errorParam 
    });

    // Handle error
    if (errorParam) {
      setError(`Connection failed: ${errorParam}`);
      return;
    }

    // Must have token from backend
    if (!token) {
      setError('No authentication token received. Please try again from BigCommerce.');
      return;
    }

    // Store the token
    localStorage.setItem('token', token);
    sessionStorage.setItem('jwtToken', token);
    if (authContext?.setToken) {
      authContext.setToken(token);
    }

    // Store BigCommerce context
    if (storeHash) {
      sessionStorage.setItem('bigcommerceStoreHash', storeHash);
      sessionStorage.setItem('platform', 'BigCommerce');
    }

    // Navigate based on what params we have
    if (siteId) {
      // Has site - go to integrations (or dashboard if setup complete)
      console.log('[BigCommerceLanding] → Integrations');
      navigate(
        `/integrations/bigcommerce` +
        `?store_hash=${storeHash}` +
        `&site_id=${siteId}` +
        `&site_token=${siteToken || ''}` +
        `&platform=BigCommerce`
      );
    } else {
      // No site ID means something went wrong or it's dashboard redirect
      console.log('[BigCommerceLanding] → Dashboard');
      navigate(`/dashboard?store_hash=${storeHash}&platform=BigCommerce`);
    }
  }, [params, navigate, authContext]);

  if (error) {
    return (
      <Frame>
        <Page>
          <Layout>
            <Layout.Section>
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4rem 2rem',
                minHeight: '60vh',
                textAlign: 'center'
              }}>
                <Text as="h2" variant="headingLg">Something went wrong</Text>
                <Text as="p" variant="bodyMd" tone="critical" style={{ marginTop: '1rem' }}>
                  {error}
                </Text>
                <Text as="p" variant="bodyMd" tone="subdued" style={{ marginTop: '1rem' }}>
                  Please try again from your BigCommerce admin panel.
                </Text>
              </div>
            </Layout.Section>
          </Layout>
        </Page>
      </Frame>
    );
  }

  return (
    <Frame>
      <Page>
        <Layout>
          <Layout.Section>
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4rem 2rem',
              minHeight: '60vh'
            }}>
              {/* BaoBrain branded spinner */}
              <div style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #6A0DAD 0%, #00A8A9 100%)',
                padding: '3px',
                marginBottom: '24px',
              }}>
                <div style={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Spinner size="small" />
                </div>
              </div>
              
              <div style={{
                background: 'linear-gradient(135deg, #6A0DAD 0%, #00A8A9 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                fontSize: '24px',
                fontWeight: '700',
                marginBottom: '8px',
              }}>
                BaoBrain
              </div>
              
              <Text as="p" variant="bodyMd" alignment="center" tone="subdued">
                Loading your analytics dashboard...
              </Text>
            </div>
          </Layout.Section>
        </Layout>
      </Page>
    </Frame>
  );
}